// A submitted review's inline comments, as the Overview's conversation
// shows them inside the review: each thread the review commented in, with
// the diff lines it was written on (GitHub's `diffHunk`) and the replies
// that followed. Read from the pull request's review threads, which the
// page already holds: nothing more is asked of GitHub.
import type { ReviewThread, ThreadComment } from '../api/queries';
import type { PrRef } from './headerTargets';
import { chipTarget, snapshotHref } from './snapshotLink';

/** A row of a diff hunk, numbered from its `@@` header. */
export interface HunkRow {
  kind: 'add' | 'del' | 'ctx';
  old: number | null;
  new: number | null;
  text: string;
}

const HEADER = /^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/;

/**
 * The last `count` rows of a diff hunk (a review comment's `diffHunk`
 * ends at the line it was written on), numbered from the hunk's header.
 * Empty when there is no hunk or it has no header.
 */
export function hunkTail(diffHunk: string | null | undefined, count: number): HunkRow[] {
  if (!diffHunk) return [];
  const lines = diffHunk.replace(/\r\n?/g, '\n').split('\n');
  const head = HEADER.exec(lines[0] ?? '');
  if (!head) return [];
  let old = Number(head[1]);
  let nu = Number(head[2]);
  const rows: HunkRow[] = [];
  for (const l of lines.slice(1)) {
    if (l.startsWith('\\')) continue;
    if (l.startsWith('+')) rows.push({ kind: 'add', old: null, new: nu++, text: l.slice(1) });
    else if (l.startsWith('-')) rows.push({ kind: 'del', old: old++, new: null, text: l.slice(1) });
    else rows.push({ kind: 'ctx', old: old++, new: nu++, text: l.slice(1) });
  }
  return rows.slice(Math.max(0, rows.length - count));
}

/** Diff rows shown above a review comment. */
export const HUNK_ROWS = 4;

export interface ReviewThreadEntry {
  thread: ReviewThread;
  /** The review's first comment in the thread and every published one after it (its own and replies). */
  comments: ThreadComment[];
  /** How many of `comments` the review wrote. */
  own: number;
  /** Published comments before the review's first: it replied to a thread begun earlier. */
  earlier: number;
  /** The diff lines the thread was begun on (its last HUNK_ROWS rows). */
  hunk: HunkRow[];
}

const published = (c: ThreadComment) => c.pullRequestReview?.state !== 'PENDING';

/**
 * The threads review `reviewId` commented in, in the order it wrote its
 * first comment in each: each from that comment on, the viewer's pending
 * comments left out.
 */
export function reviewThreads(reviewId: string, threads: readonly ReviewThread[]): ReviewThreadEntry[] {
  const out: (ReviewThreadEntry & { at: string })[] = [];
  for (const thread of threads) {
    const list = thread.comments.nodes.filter(published);
    const first = list.findIndex((c) => c.pullRequestReview?.id === reviewId);
    if (first < 0) continue;
    const comments = list.slice(first);
    const source = list[first].diffHunk ? list[first] : list[0];
    out.push({
      thread,
      comments,
      own: comments.filter((c) => c.pullRequestReview?.id === reviewId).length,
      earlier: first,
      hunk: hunkTail(source?.diffHunk, HUNK_ROWS),
      at: list[first].createdAt,
    });
  }
  out.sort((a, b) => Date.parse(a.at) - Date.parse(b.at) || a.thread.path.localeCompare(b.thread.path));
  return out.map(({ at: _, ...e }) => e);
}

/** Where a thread is: `path:line` (`L` on the old side, a range when it spans lines), the line it was on, or the file. */
export function threadPlace(t: ReviewThread): string {
  if (t.subjectType === 'FILE') return t.path;
  if (t.line === null) return t.originalLine ? `${t.path} (was line ${t.originalLine})` : t.path;
  const side = t.diffSide === 'LEFT' ? 'L' : '';
  const range = t.startLine && t.startLine !== t.line ? `${t.startLine}–${t.line}` : String(t.line);
  return `${t.path}:${side}${range}`;
}

/**
 * The Files view showing comment `comment` of thread `t`: at its lines on
 * the head while it has them there, else on the snapshot its first comment
 * was written on (an outdated thread), as a comment's snapshot chip opens it.
 */
export function commentHref(t: ReviewThread, comment: string, pr: PrRef, head: string, query: URLSearchParams): string {
  const at = chipTarget(t, head, head);
  return snapshotHref(pr, { sha: at.sha, head, path: t.path, lines: at.spot, thread: t.id, comment }, query);
}
